/**
 * Shared UI + animation primitives for the v2 landing redesign.
 *
 * Every landing section imports its building blocks from here so the redesign
 * stays consistent and DRY. Dependency-free beyond framer-motion + Tailwind.
 * Aesthetic: trustworthy, precise, calm, restrained motion (JP-B2B / 安心感).
 *
 * Conventions honored (CONVENTIONS ARE LAW):
 *  - Section shell `relative py-20 md:py-28`, eyebrow → h2.display.serif-jp →
 *    muted sub, centered or start-aligned header block.
 *  - Shared variants from ./animations; reduced-motion gating via ./hooks.
 *  - Theme-token surfaces (bg-card / text-foreground / border-border …),
 *    single indigo accent, logical props (ps/pe/ms/me, start/end, origin-start).
 */
import {
  type CSSProperties,
  type ElementType,
  type ReactNode,
  useRef,
} from 'react';
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  type Variants,
} from 'framer-motion';
import {
  EASE_STANDARD,
  fadeInUp,
  sectionViewport,
  staggerContainer,
} from './animations';
import { usePrefersReducedMotion, useCountUp } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────
// Reveal — whileInView scroll reveal, reduced-motion safe.
// ─────────────────────────────────────────────────────────────────────────────

export interface RevealProps {
  children: ReactNode;
  /** Variant to animate with. Defaults to the workhorse `fadeInUp`. */
  variants?: Variants;
  /** Stagger delay (s) for the parent timeline. Mutually exclusive-ish with variants. */
  delay?: number;
  /** Render as a different element (e.g. 'li', 'span'). Default 'div'. */
  as?: 'div' | 'section' | 'li' | 'span' | 'p' | 'article' | 'header';
  className?: string;
  /** Re-run every time it enters the viewport instead of once. */
  repeat?: boolean;
  style?: CSSProperties;
}

/**
 * Drop-in scroll reveal. framer already honors prefers-reduced-motion for
 * whileInView, so reduced-motion users simply see the end-state.
 *
 *   <Reveal><h3>…</h3></Reveal>
 *   <Reveal variants={fadeInLeft} delay={0.1}>…</Reveal>
 */
export function Reveal({
  children,
  variants = fadeInUp,
  delay,
  as = 'div',
  className,
  repeat = false,
  style,
}: RevealProps) {
  const MotionTag = motion[as] as typeof motion.div;
  return (
    <MotionTag
      initial="hidden"
      whileInView="visible"
      viewport={repeat ? { margin: '-80px' } : sectionViewport}
      variants={variants}
      transition={delay != null ? { delay, duration: 0.6, ease: EASE_STANDARD } : undefined}
      className={className}
      style={style}
    >
      {children}
    </MotionTag>
  );
}

/**
 * Stagger parent — wrap a group of bare `<Reveal>` / `motion` children and the
 * parent drives the timing (children need only `variants`, no transition).
 *
 *   <Stagger className="grid gap-6">
 *     {items.map(i => <Reveal key={i.id}>…</Reveal>)}
 *   </Stagger>
 */
export function Stagger({
  children,
  className,
  as = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'ul' | 'section';
}) {
  const MotionTag = motion[as] as typeof motion.div;
  return (
    <MotionTag
      initial="hidden"
      whileInView="visible"
      viewport={sectionViewport}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </MotionTag>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SectionShell — consistent vertical rhythm + header block.
// ─────────────────────────────────────────────────────────────────────────────

export interface SectionShellProps {
  children: ReactNode;
  /** Anchor / id for in-page nav. */
  id?: string;
  /** Eyebrow kicker — text-xs uppercase tracking-[0.2em] text-primary. */
  eyebrow?: ReactNode;
  /** h2 — rendered `display serif-jp`. JP auto-swaps to serif via html[lang^='ja']. */
  heading?: ReactNode;
  /** Muted sub-header line. */
  subhead?: ReactNode;
  /** Center the header block (and pass through to content alignment helpers). */
  align?: 'start' | 'center';
  /** Navy-leaning trust panel surface (bg-muted/30) vs default background. */
  tone?: 'default' | 'muted';
  /** Max content width. Default '7xl'. */
  width?: '5xl' | '6xl' | '7xl';
  /** Extra classes on the <section>. */
  className?: string;
  /** Extra classes on the inner container. */
  innerClassName?: string;
  /** Absolutely-positioned background layer rendered behind content (e.g. <GradientMesh/>). */
  background?: ReactNode;
}

const WIDTH_MAP = { '5xl': 'max-w-5xl', '6xl': 'max-w-6xl', '7xl': 'max-w-7xl' } as const;

/**
 * The canonical section frame. Renders the eyebrow → h2.display.serif-jp →
 * muted-sub header block with the project's standard spacing, then children.
 *
 *   <SectionShell eyebrow={t('x.eyebrow')} heading={t('x.title')} subhead={t('x.sub')}>
 *     …content…
 *   </SectionShell>
 */
export function SectionShell({
  children,
  id,
  eyebrow,
  heading,
  subhead,
  align = 'start',
  tone = 'default',
  width = '7xl',
  className = '',
  innerClassName = '',
  background,
}: SectionShellProps) {
  const hasHeader = eyebrow || heading || subhead;
  const centered = align === 'center';
  return (
    <section
      id={id}
      className={`relative py-20 md:py-28 ${tone === 'muted' ? 'bg-muted/30' : ''} ${className}`}
    >
      {background}
      <div className={`container relative z-10 mx-auto ${WIDTH_MAP[width]} px-4 sm:px-6 lg:px-8`}>
        {hasHeader && (
          <Stagger
            className={`mb-12 ${centered ? 'mx-auto max-w-3xl text-center' : 'max-w-3xl'}`}
          >
            {eyebrow && (
              <Reveal as="p" className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
                {eyebrow}
              </Reveal>
            )}
            {heading && (
              <Reveal
                as="div"
                className="display serif-jp mt-2 text-3xl md:text-5xl font-semibold tracking-tight text-balance"
              >
                <h2>{heading}</h2>
              </Reveal>
            )}
            {subhead && (
              <Reveal
                as="p"
                className={`mt-3 text-base md:text-lg text-muted-foreground leading-relaxed text-pretty ${
                  centered ? 'mx-auto' : ''
                } max-w-2xl`}
              >
                {subhead}
              </Reveal>
            )}
          </Stagger>
        )}
        <div className={innerClassName}>{children}</div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GradientMesh / AuroraBackground — CSS-driven ambient background.
// ─────────────────────────────────────────────────────────────────────────────

export interface GradientMeshProps {
  /** Show the masked 1px grid texture (quiet, JP-appropriate). Default true. */
  grid?: boolean;
  /** Drifting indigo→fuchsia→sky blobs. Default true; auto-off under reduced-motion. */
  blobs?: boolean;
  /** Fade the bottom edge into the page background. Default true. */
  vignette?: boolean;
  className?: string;
}

/**
 * Pointer-events-none ambient layer: soft mesh + masked grid + slow-drifting
 * blobs. Blob drift uses CSS `animate-blob` (off the JS thread) and disables
 * itself under prefers-reduced-motion. Place inside a `relative isolate` parent
 * (or pass as `<SectionShell background={…}/>`).
 *
 *   <SectionShell background={<GradientMesh />}>…</SectionShell>
 */
export function GradientMesh({
  grid = true,
  blobs = true,
  vignette = true,
  className = '',
}: GradientMeshProps) {
  const reduced = usePrefersReducedMotion();
  return (
    <div aria-hidden className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
      <div className="absolute inset-0 gradient-mesh" />
      {grid && <div className="absolute inset-0 grid-bg opacity-60" />}
      {blobs && (
        <>
          <div
            className={`absolute -top-32 -start-32 h-[34rem] w-[34rem] rounded-full bg-gradient-to-br from-indigo-400/30 to-violet-400/20 dark:from-indigo-500/15 dark:to-violet-500/10 blur-3xl ${
              reduced ? '' : 'animate-blob'
            }`}
          />
          <div
            className={`absolute top-1/4 -end-32 h-[32rem] w-[32rem] rounded-full bg-gradient-to-br from-fuchsia-300/25 to-indigo-300/25 dark:from-fuchsia-500/12 dark:to-indigo-500/12 blur-3xl ${
              reduced ? '' : 'animate-blob'
            }`}
            style={reduced ? undefined : { animationDelay: '6s' }}
          />
          <div
            className={`absolute -bottom-32 start-1/3 h-[26rem] w-[26rem] rounded-full bg-gradient-to-br from-sky-300/20 to-emerald-200/15 dark:from-sky-500/10 dark:to-emerald-500/8 blur-3xl ${
              reduced ? '' : 'animate-blob'
            }`}
            style={reduced ? undefined : { animationDelay: '12s' }}
          />
        </>
      )}
      {vignette && (
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background/40" />
      )}
    </div>
  );
}

/** Alias — same component, named for the aurora/mesh metaphor. */
export const AuroraBackground = GradientMesh;

// ─────────────────────────────────────────────────────────────────────────────
// CountUp — animated number that tweens when it enters the viewport.
// ─────────────────────────────────────────────────────────────────────────────

export interface CountUpProps {
  /** Final numeric value. */
  to: number;
  /** Tween duration (s). Default 1.2. */
  duration?: number;
  /** Decimal places. Default 0. */
  decimals?: number;
  /** Rendered before the number (e.g. '¥', '$'). */
  prefix?: ReactNode;
  /** Rendered after the number (e.g. '%', '日', 'k'). */
  suffix?: ReactNode;
  className?: string;
  /** Element to render. Default 'span'. */
  as?: 'span' | 'p' | 'div';
}

/**
 * Tabular count-up. Counts from 0 → `to` the first time it scrolls into view,
 * then holds. Reduced-motion renders the final value instantly. Always carries
 * the `.num` class for tabular Latin figures.
 *
 *   <CountUp to={94} suffix="%" className="text-4xl font-bold gradient-text" />
 *   <CountUp to={348284} prefix="¥" />
 */
export function CountUp({
  to,
  duration = 1.2,
  decimals = 0,
  prefix,
  suffix,
  className = '',
  as = 'span',
}: CountUpProps) {
  const [value, begin] = useCountUp(to, { duration, decimals });
  const MotionTag = motion[as] as typeof motion.span;
  return (
    <MotionTag
      onViewportEnter={begin}
      viewport={{ once: true, margin: '-40px' }}
      className={`num ${className}`}
    >
      {prefix}
      {value}
      {suffix}
    </MotionTag>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// GlassCard — resting glass surface with a gentle lift on hover.
// ─────────────────────────────────────────────────────────────────────────────

export interface GlassCardProps {
  children: ReactNode;
  className?: string;
  /** Enable the -2px hover lift. Default true; auto-off under reduced-motion. */
  hover?: boolean;
  /** Stronger frosted variant. Default false. */
  strong?: boolean;
  as?: ElementType;
}

/**
 * Token-driven glass panel — rounded, soft shadow at rest, gentle lift on hover.
 *
 *   <GlassCard className="p-6">…</GlassCard>
 */
export function GlassCard({
  children,
  className = '',
  hover = true,
  strong = false,
  as,
}: GlassCardProps) {
  const reduced = usePrefersReducedMotion();
  const MotionTag = (as ? motion(as as ElementType) : motion.div) as typeof motion.div;
  const enableHover = hover && !reduced;
  return (
    <MotionTag
      whileHover={enableHover ? { y: -2 } : undefined}
      transition={{ type: 'spring', stiffness: 120, damping: 25 }}
      className={`rounded-2xl border border-border ${
        strong ? 'glass-strong' : 'glass'
      } shadow-soft ${enableHover ? 'hover:shadow-card-lift' : ''} transition-shadow duration-200 ${className}`}
    >
      {children}
    </MotionTag>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TiltCard — ±4deg mouse-tilt 3D card (disabled on touch + reduced-motion).
// ─────────────────────────────────────────────────────────────────────────────

export interface TiltCardProps {
  children: ReactNode;
  className?: string;
  /** Max tilt in degrees. Default 4. */
  maxTilt?: number;
  /** Render a reactive radial indigo glow that tracks the cursor. Default true. */
  glow?: boolean;
}

/**
 * Reactive 3D tilt. useMotionValue → useSpring(120/25) → useTransform to
 * rotateX/rotateY (±maxTilt°), perspective 1200px, preserve-3d. Falls back to a
 * plain GlassCard on touch devices or under prefers-reduced-motion.
 *
 *   <TiltCard className="p-6"><HeroProductMock /></TiltCard>
 */
export function TiltCard({ children, className = '', maxTilt = 4, glow = true }: TiltCardProps) {
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLDivElement>(null);

  const mvX = useMotionValue(0.5);
  const mvY = useMotionValue(0.5);
  const sx = useSpring(mvX, { stiffness: 120, damping: 25 });
  const sy = useSpring(mvY, { stiffness: 120, damping: 25 });

  const rotateY = useTransform(sx, [0, 1], [-maxTilt, maxTilt]);
  const rotateX = useTransform(sy, [0, 1], [maxTilt, -maxTilt]);
  const glowX = useTransform(sx, [0, 1], ['0%', '100%']);
  const glowY = useTransform(sy, [0, 1], ['0%', '100%']);
  // Hoisted above the early return below so this hook runs unconditionally on
  // every render (Rules of Hooks). Only *read* when `glow` is set.
  const glowBg = useTransform(
    [glowX, glowY],
    ([gx, gy]) => `radial-gradient(60% 60% at ${gx} ${gy}, rgba(26,26,255,0.12), transparent 70%)`,
  );

  // Coarse pointer (touch) → no tilt. SSR-safe (matchMedia may be undefined).
  const isTouch =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(pointer: coarse)').matches;

  if (reduced || isTouch) {
    return <GlassCard className={className}>{children}</GlassCard>;
  }

  const handleMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    mvX.set((e.clientX - rect.left) / rect.width);
    mvY.set((e.clientY - rect.top) / rect.height);
  };
  const handleLeave = () => {
    mvX.set(0.5);
    mvY.set(0.5);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{ perspective: 1200 }}
      className={className}
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        className="relative h-full w-full rounded-2xl border border-border glass-strong shadow-card-lift"
      >
        {glow && (
          <motion.div
            aria-hidden
            className="pointer-events-none absolute inset-0 rounded-2xl"
            style={{ background: glowBg }}
          />
        )}
        <div className="relative" style={{ transform: 'translateZ(40px)' }}>
          {children}
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MockChrome — extracted browser-bar frame (dots + keenez.com + live dot).
// ─────────────────────────────────────────────────────────────────────────────

export interface MockChromeProps {
  children: ReactNode;
  /** URL shown centered in the chrome bar. Default 'keenez.com'. */
  url?: string;
  /** Show the emerald live dot + label. Default true. */
  live?: boolean;
  /** Live label text. Default 'live'. */
  liveLabel?: string;
  className?: string;
  /** Classes on the body region under the chrome bar. */
  bodyClassName?: string;
}

/**
 * The shared product-theater browser frame, reused by hero, ai-copilot,
 * payroll and border-buddy mocks. The live dot does not loop (calm); it is a
 * static emerald trust signal.
 *
 *   <MockChrome url="keenez.com/payroll"><PayrollTable/></MockChrome>
 */
export function MockChrome({
  children,
  url = 'keenez.com',
  live = true,
  liveLabel = 'live',
  className = '',
  bodyClassName = '',
}: MockChromeProps) {
  return (
    <div className={`rounded-3xl glass-strong shadow-card-lift overflow-hidden ${className}`}>
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-2.5 bg-background/60">
        <div className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-rose-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/80" />
        </div>
        <span className="num text-[10px] text-muted-foreground tracking-widest">{url}</span>
        <div className="flex items-center gap-1.5">
          {live ? (
            <>
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              <span className="text-[10px] text-muted-foreground">{liveLabel}</span>
            </>
          ) : (
            <span className="h-1.5 w-1.5" />
          )}
        </div>
      </div>
      <div className={bodyClassName}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Marquee — CSS-keyframe logo/trust strip, pauses under reduced-motion.
// ─────────────────────────────────────────────────────────────────────────────

export interface MarqueeProps {
  children: ReactNode;
  /** Seconds per full loop. Default 40 (calm). */
  duration?: number;
  /** Scroll toward the end side instead of the start side. */
  reverse?: boolean;
  /** Fade both edges into the background. Default true. */
  fade?: boolean;
  className?: string;
}

/**
 * Edge-faded marquee using the existing `animate-marquee` keyframe (off the JS
 * thread). Content is duplicated for a seamless loop; pauses on hover and is
 * rendered static (single, non-animated row) under prefers-reduced-motion.
 *
 *   <Marquee>{logos.map(l => <img key={l.id} … />)}</Marquee>
 */
export function Marquee({
  children,
  duration = 40,
  reverse = false,
  fade = true,
  className = '',
}: MarqueeProps) {
  const reduced = usePrefersReducedMotion();

  if (reduced) {
    return (
      <div className={`flex flex-wrap items-center justify-center gap-x-10 gap-y-4 ${className}`}>
        {children}
      </div>
    );
  }

  const maskStyle: CSSProperties = fade
    ? {
        WebkitMaskImage:
          'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
        maskImage: 'linear-gradient(to right, transparent, black 8%, black 92%, transparent)',
      }
    : {};

  return (
    <div className={`group relative overflow-hidden ${className}`} style={maskStyle}>
      <div
        className="flex w-max items-center gap-x-10 animate-marquee group-hover:[animation-play-state:paused]"
        style={
          {
            '--marquee-duration': `${duration}s`,
            animationDirection: reverse ? 'reverse' : 'normal',
          } as CSSProperties
        }
      >
        <div className="flex shrink-0 items-center gap-x-10">{children}</div>
        <div aria-hidden className="flex shrink-0 items-center gap-x-10">
          {children}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Eyebrow — standalone kicker (when not using SectionShell's header block).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The standard eyebrow kicker, for inline use outside SectionShell.
 *   <Eyebrow>{t('x.eyebrow')}</Eyebrow>
 */
export function Eyebrow({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <p className={`text-xs uppercase tracking-[0.2em] text-primary font-semibold ${className}`}>
      {children}
    </p>
  );
}
